import React from 'react';
import { logoutCleanup } from '../../auth';
import { startInPersonSession } from '../../lib/api/envelope/envelope-api';
import { withReturnToSender, type IInPersonCandidate } from './utils';

// Required, not optional, so a new entry point cannot go untracked.
export type InPersonTouchpoint = 'envelopeList' | 'envelopeHistory';

function messageFor(e: any): string {
  const status = e?.response?.status;
  if (status === 409) return "It is not this person's turn to sign yet. Refresh the page and try again.";
  if (status === 403) return 'You do not have access to start signing on this envelope.';
  if (status === 404) return 'We could not find that signer on this envelope.';
  return e?.message || 'Something went wrong starting the signing session.';
}

/**
 * Hands the device to a signer for an in-person session. requestHandOff only
 * names who is about to sign; nothing starts until confirmHandOff, and there is
 * no way to start a session that skips it.
 */
export function useInPersonSigning() {
  const [pendingHandOff, setPendingHandOff] = React.useState<{
    signer: IInPersonCandidate;
    envelopeId: string;
    touchpoint: InPersonTouchpoint;
  } | null>(null);
  const [startingSignerId, setStartingSignerId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const inFlight = React.useRef(false);
  const mounted = React.useRef(true);

  React.useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  // Clears the error so the next signer's confirmation does not open showing
  // the last one's failure.
  const requestHandOff = React.useCallback(
    (envelopeId: string, signer: IInPersonCandidate, touchpoint: InPersonTouchpoint) => {
      setError(null);
      setPendingHandOff({ signer, envelopeId, touchpoint });
    },
    []
  );

  const cancelHandOff = React.useCallback(() => {
    setError(null);
    setPendingHandOff(null);
  }, []);

  const confirmHandOff = React.useCallback(async () => {
    if (!pendingHandOff) return;
    const { envelopeId, signer, touchpoint } = pendingHandOff;
    const signerId = signer.signerId;

    // A second session would strand the first one's token.
    if (inFlight.current) return;
    inFlight.current = true;
    setError(null);
    setStartingSignerId(signerId);

    // Optional call: heap is absent behind ad blockers and in local dev.
    window.heap?.track('inPersonSigningStarted', { touchpoint, envelopeId, signerId });

    try {
      const session = await startInPersonSession(envelopeId, signerId);
      if (!session?.signingUrl) throw new Error('The signing session came back without a link.');
      logoutCleanup();
      window.location.assign(withReturnToSender(session.signingUrl));
    } catch (e) {
      inFlight.current = false;
      if (!mounted.current) return;
      setStartingSignerId(null);
      setError(messageFor(e));
    }
  }, [pendingHandOff]);

  // Memoized so consumers can put this in a dependency array.
  return React.useMemo(
    () => ({
      requestHandOff,
      confirmHandOff,
      cancelHandOff,
      pendingHandOff: pendingHandOff?.signer ?? null,
      startingSignerId,
      isStarting: startingSignerId !== null,
      error,
    }),
    [requestHandOff, confirmHandOff, cancelHandOff, pendingHandOff, startingSignerId, error]
  );
}
